(() => {
  'use strict';

let pickupAvailabilityInstance = 0;

if (!customElements.get('pickup-availability')) {
  customElements.define('pickup-availability', class PickupAvailability extends HTMLElement {
    constructor() {
      super();
      pickupAvailabilityInstance += 1;
      this.pickupInstanceId = `pickup-instance-${pickupAvailabilityInstance}`;
      this.activeDrawerOwner = '';
      this.availabilityRequestGeneration = 0;
      this.availabilityController = null;
      this.errorHtml = null;
      this.onClickRefreshList = this.onClickRefreshList.bind(this);
    }

    connectedCallback() {
      if (this.availabilityConnected) return;
      this.availabilityConnected = true;
      if (!this.dataset.section) {
        this.dataset.section = this.parentElement?.querySelector?.('product-form[data-section]')?.dataset?.section
          || this.closest?.('[data-section]')?.dataset?.section
          || '';
      }
      const template = this.querySelector('template');
      this.errorHtml = template?.content?.firstElementChild?.cloneNode(true) || this.errorHtml;
      if (this.hasAttribute('available') && this.dataset.variantId) {
        this.fetchAvailability(this.dataset.variantId);
      }
    }

    disconnectedCallback() {
      this.availabilityConnected = false;
      this.cancelAvailabilityRequest();
      this.removeDrawer();
    }

    cancelAvailabilityRequest() {
      this.availabilityRequestGeneration += 1;
      this.availabilityController?.abort();
      this.availabilityController = null;
    }

    drawerOwnerIdentity(variantId = this.dataset.variantId) {
      const sectionIdentity = this.dataset.section || this.pickupInstanceId;
      return `${sectionIdentity}::${String(variantId || '')}`;
    }

    ownedDrawer() {
      if (!this.activeDrawerOwner) return null;
      return [...(document.querySelectorAll?.('pickup-availability-drawer') || [])]
        .find((drawer) => drawer.dataset?.pickupOwner === this.activeDrawerOwner) || null;
    }

    removeDrawer() {
      const drawer = this.ownedDrawer();
      if (drawer?.hasAttribute?.('open')) drawer.hide?.();
      drawer?.remove?.();
      this.activeDrawerOwner = '';
    }

    clearRenderedAvailability() {
      this.innerHTML = '';
      this.removeAttribute('available');
      this.removeDrawer();
    }

    clearAvailability(variantId = '') {
      this.cancelAvailabilityRequest();
      this.clearRenderedAvailability();
      this.dataset.variantId = String(variantId || '');
    }

    updateVariant(variantId, available = false) {
      const nextVariantId = String(variantId || '');
      if (!nextVariantId || !available) {
        this.clearAvailability(nextVariantId);
        return Promise.resolve(false);
      }
      return this.fetchAvailability(nextVariantId);
    }

    requestIsCurrent(generation, variantId, controller) {
      return this.availabilityRequestGeneration === generation
        && this.availabilityController === controller
        && !controller.signal.aborted
        && this.dataset.variantId === variantId;
    }

    fetchAvailability(variantId) {
      const nextVariantId = String(variantId || '');
      if (!nextVariantId) {
        this.clearAvailability();
        return Promise.resolve(false);
      }

      this.cancelAvailabilityRequest();
      this.clearRenderedAvailability();
      this.dataset.variantId = nextVariantId;
      const generation = this.availabilityRequestGeneration;
      const controller = new AbortController();
      this.availabilityController = controller;
      let rootUrl = this.dataset.rootUrl || window.Shopify?.routes?.root || '/';
      if (!rootUrl.endsWith('/')) rootUrl += '/';
      const variantSectionUrl = `${rootUrl}variants/${nextVariantId}/?section_id=pickup-availability`;

      return fetch(variantSectionUrl, { signal: controller.signal })
        .then((response) => {
          if (!response.ok) throw new Error('Pickup availability request failed.');
          return response.text();
        })
        .then((text) => {
          if (!this.requestIsCurrent(generation, nextVariantId, controller)) return false;
          const sectionInnerHTML = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('.shopify-section');
          if (!this.requestIsCurrent(generation, nextVariantId, controller)) return false;
          return this.renderPreview(sectionInnerHTML);
        })
        .catch((error) => {
          if (error?.name === 'AbortError' || !this.requestIsCurrent(generation, nextVariantId, controller)) return false;
          this.renderError();
          return false;
        })
        .finally(() => {
          if (this.availabilityController === controller) this.availabilityController = null;
        });
    }

    onClickRefreshList() {
      return this.fetchAvailability(this.dataset.variantId);
    }

    renderError() {
      this.clearRenderedAvailability();
      if (!this.errorHtml) return;
      const error = this.errorHtml.cloneNode?.(true) || this.errorHtml;
      this.appendChild(error);
      this.setAttribute('available', '');

      this.querySelector('button')?.addEventListener('click', this.onClickRefreshList);
    }

    renderPreview(sectionInnerHTML) {
      const preview = sectionInnerHTML?.querySelector('pickup-availability-preview');
      if (!preview) {
        this.clearRenderedAvailability();
        return false;
      }

      this.clearRenderedAvailability();
      this.innerHTML = preview.outerHTML;
      this.setAttribute('available', '');

      const drawer = sectionInnerHTML.querySelector('pickup-availability-drawer');
      if (drawer) {
        const owner = this.drawerOwnerIdentity();
        const identityToken = owner.replace(/[^a-zA-Z0-9_-]/g, '-');
        const drawerId = `PickupAvailabilityDrawer-${identityToken}`;
        const headingId = `PickupAvailabilityHeading-${identityToken}`;
        this.activeDrawerOwner = owner;
        drawer.dataset.pickupOwner = owner;
        drawer.id = drawerId;
        const heading = drawer.querySelector?.('#PickupAvailabilityHeading');
        if (heading) heading.id = headingId;
        drawer.setAttribute?.('aria-labelledby', headingId);
        document.body.appendChild(drawer);
      }

      const button = this.querySelector('button');
      if (button) {
        const drawer = this.ownedDrawer();
        if (drawer) {
          button.id = `ShowPickupAvailabilityDrawer-${this.activeDrawerOwner.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
          button.setAttribute?.('aria-controls', drawer.id);
          button.dataset.pickupDrawerOwner = this.activeDrawerOwner;
        }
        button.addEventListener('click', () => {
          this.ownedDrawer()?.show?.(button);
        });
      }
      return true;
    }
  });
}

if (!customElements.get('pickup-availability-drawer')) {
  customElements.define('pickup-availability-drawer', class PickupAvailabilityDrawer extends HTMLElement {
    constructor() {
      super();

      this.onBodyClick = this.handleBodyClick.bind(this);

      this.querySelector('button').addEventListener('click', () => {
        this.hide();
      });

      this.addEventListener('keyup', (event) => {
        if(event.code.toUpperCase() === 'ESCAPE') this.hide();
      });
    }

    handleBodyClick(evt) {
      const target = evt.target;
      const targetDrawer = target?.closest?.('pickup-availability-drawer');
      const targetTrigger = target?.closest?.('[aria-controls]');
      if (target !== this
        && targetDrawer !== this
        && targetTrigger?.getAttribute?.('aria-controls') !== this.id) {
        this.hide();
      }
    }

    hide() {
      this.removeAttribute('open');
      document.body.removeEventListener('click', this.onBodyClick);
      document.body.classList.remove('overflow-hidden');
      removeTrapFocus(this.focusElement);
    }

    show(focusElement) {
      this.focusElement = focusElement;
      this.setAttribute('open', '');
      document.body.addEventListener('click', this.onBodyClick);
      document.body.classList.add('overflow-hidden');
      trapFocus(this);
    }
  });
}
})();
